import { createFileRoute, useRouter } from '@tanstack/react-router';
import { Button } from 'components/Button';
import { FilterControls, type FilterOption } from 'components/FiltersControls';
import { Heading } from 'components/Heading';
import { ModalButton } from 'components/ModalButton';
import { Table } from 'components/Table';
import { TinySkinPreview } from 'components/TinySkinPreview';
import { emitMessage } from 'engine/api';
import { appConfig } from 'engine/appConfig';
import { GameTypeOptions } from 'forms/GameFormConstants';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { type GameView } from 'shared/modelTypes/game';

export const Route = createFileRoute('/dashboard/games/')({
	component: RouteComponent,

	loader: async () => {
		const data = await emitMessage({ kind: 'get-game-list' });
		return {
			games: data.gameViews ?? [],
			skins: (await emitMessage({ kind: 'skin-list' })).skins ?? [],
		};
	},
});

function RouteComponent() {
	const data = Route.useLoaderData();
	const router = useRouter();

	const [games, setGames] = useState(data.games);

	const filterOptions: FilterOption<GameView>[] = [
		{
			key: 'lang',
			label: 'lang',
			values: [...new Set(games?.map((g) => g.language))].map((language) => ({
				label: `${language}`,
				value: language,
			})),
			getValue: (game: GameView) => game.language ?? 'en',
		},
	];

	if (GameTypeOptions.length > 1) {
		filterOptions.unshift({
			key: 'type',
			label: 'Type',
			values: GameTypeOptions.map((x) => ({ value: x.label, label: x.label })),
			getValue: (game: GameView) => game.type ?? '',
		});
	}

	if (appConfig.permissions.allowSkins) {
		filterOptions.push({
			key: 'skin',
			label: 'Skin',
			values: (data?.skins?.sort((a, b) => a.skin.name.localeCompare(b.skin.name, 'en', { sensitivity: 'base' })) || []).map(
				(skin) => ({ label: skin.skin.name, value: skin.id }),
			),
			getValue: (game: GameView) => game.skin?.id ?? '',
		});
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="border-yellow flex items-center gap-3">
				<Heading>Games</Heading>
				<Button to="/dashboard/games/new">
					New Game <Plus className="size-4" />
				</Button>
			</div>

			<FilterControls
				filterOptions={filterOptions}
				items={data.games || []}
				getSearchableText={(s) => s.name}
				sortOptions={[
					{
						key: 'name',
						label: 'Name',
						sortFn: (a, b) => a.name.localeCompare(b.name),
					},
					{
						key: 'createdAt',
						label: 'Creation date',
						sortFn: (a, b) => a.createdAt.localeCompare(b.createdAt),
					},
					{
						key: 'updatedAt',
						label: 'Last updated',
						sortFn: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
					},
				]}
				onChanged={setGames}
			/>
			{!data.games && <span>Could not get games.</span>}
			{data.games && (
				<Table
					className="w-fit"
					headers={['Name', 'Type', 'Lang', appConfig.permissions.allowSkins ? 'Skin' : '', 'Actions']}
					rows={games!.map((g) => {
						return [
							// ----------- NAME -----------
							<span className="font-bold">{g.name}</span>,
							// ----------- TYPE -----------
							<span>{g.type}</span>,
							// ---------- Language ----------
							<span className="text-md size-full justify-center text-center align-middle">
								<img className="size-4" src={`/flags/${g.language}.svg`} />
							</span>,
							// ----------- SKIN -----------
							appConfig.permissions.allowSkins ? <TinySkinPreview skin={g.skin?.skin} /> : <></>,
							// ----------- ACTIONS -----------
							<div className="flex items-center gap-2">
								<Button to={'/dashboard/games/edit/$id'} params={{ id: g.id }} mode="primary" className="p-1">
									<Pencil />
								</Button>
								<ModalButton
									title={`Make a copy of "${g.name}"?`}
									onAccept={async () => {
										await emitMessage({ kind: 'game-copy', id: g.id });
										router.navigate({ to: '/dashboard/games' });
									}}
									mode={'secondary'}
									className="border-2 border-gray-200 p-1"
								>
									<Copy />
								</ModalButton>
								<ModalButton
									title={`Are you sure you want to delete "${g.name}"?`}
									onAccept={async () => {
										await emitMessage({ kind: 'game-delete', id: g.id });
										router.navigate({ to: '/dashboard/games' });
									}}
									mode={'secondary'}
									className="border-2 border-gray-200 p-1"
								>
									<Trash2 />
								</ModalButton>
							</div>,
						];
					})}
				></Table>
			)}
		</div>
	);
}
